import { Roles } from '@common/types/user';
import { QUESTIONNAIRE_ID_MAX_LENGTH } from '@constants/common';
import { EventLocation, EventTypes } from '@event/types/event';
import { getTextFromHTML } from '@utils/common';
import * as Yup from 'yup';

const requiredText = (field: string) => `Please fill in ${field} or unpublish the event before saving`;

const EventCreationSchema = Yup.object({
    type: Yup.string().oneOf(Object.values(EventLocation)).required('Required'),
    privacy: Yup.string().oneOf(['private', 'public']).required('Required'),
    name: Yup.string().required('Required'),
});

const EventEditSchema = Yup.object({
    type: Yup.string().oneOf(Object.values(EventTypes)),
    published: Yup.boolean(),
    name: Yup.string().required("The event title is required, without it you can't save this event"),
    subtitle: Yup.string().nullable(),
    date_start: Yup.date()
        .nullable()
        .max(Yup.ref('date_end'), 'Start date must be before end date')
        .when('published', ([published], schema) =>
            published ? schema.required(requiredText('date')).typeError('Invalid start date') : schema
        ),
    date_end: Yup.date()
        .nullable()
        .min(Yup.ref('date_start'), 'End date must be after start date')
        .when('published', ([published], schema) =>
            published ? schema.required(requiredText('date')).typeError('Invalid end date') : schema
        ),
    time_zone: Yup.string().when(['published'], ([published], schema) =>
        published ? schema.required(requiredText('time zone')) : schema
    ),
    description: Yup.string()
        .nullable()
        .when('published', ([published], schema) =>
            published
                ? schema.test(
                      'len',
                      requiredText('description'),
                      (val) => Number(val ? getTextFromHTML(val).length : val?.length) > 0
                  )
                : schema
        ),
    language_spoken: Yup.string().nullable(),
    country: Yup.string()
        .nullable()
        .when(['published', 'type'], ([published, type], schema) =>
            published && [EventTypes.PUBLIC_ONSITE, EventTypes.PRIVATE_ONSITE].includes(type)
                ? schema.required(requiredText('country'))
                : schema
        ),
    location: Yup.string()
        .nullable()
        .when(['published', 'type'], ([published, type], schema) =>
            published && [EventTypes.PUBLIC_ONSITE, EventTypes.PRIVATE_ONSITE].includes(type)
                ? schema.required(requiredText('location address'))
                : schema
        ),
    room_url: Yup.string().nullable().url('Enter a valid URL'),
    dropbox_link: Yup.string().nullable().url('Enter a valid URL'),
    questionnaire_id: Yup.string()
        .nullable()
        .max(QUESTIONNAIRE_ID_MAX_LENGTH, 'Questionnaire ID must be at most 20 characters')
        .when(['enable_questionnaire'], ([enableQuestionnaire], schema) =>
            !!enableQuestionnaire ? schema.required('Questionnaire ID is required when enabled') : schema
        ),
    max_capacity: Yup.number()
        .typeError('Invalid number')
        .positive('Max capacity must be positive')
        .integer('Max capacity must be integer')
        .nullable()
        .test(
            'is-greater-than-attendees',
            'Max capacity needs to be higher than current attendees',
            (value, context) => {
                const { attendees } = context.parent;
                if (value !== null && value !== undefined) {
                    return value >= attendees;
                }
                return true;
            }
        ),
    registration_url: Yup.string()
        .nullable()
        .url('Enter a valid URL')
        .when(['enable_external_registration'], ([enableRegistration], schema) =>
            !!enableRegistration ? schema.required('External registration URL is required when enabled') : schema
        ),
    city: Yup.string().optional(),
    custom_location: Yup.string().nullable(),
    possible_subscribers: Yup.array()
        .of(Yup.string())
        .when(['type'], ([type], schema) =>
            [EventTypes.PRIVATE_ONSITE, EventTypes.PRIVATE_VIRTUAL].includes(type)
                ? schema
                : schema.test('at-least-one-role', 'At least 1 role must be selected', function (value) {
                      if (!value) return false;
                      const nonVictronRoles = value.filter((role) => role !== Roles.VICTRON);
                      return nonVictronRoles.length > 0;
                  })
        ),
});

export { EventCreationSchema, EventEditSchema };
